import $ from 'jquery';

/**
 * Performs a REST Call
 * 
 * @return null
 */

export const restCall = ( endPoint = null, data = null, onSuccess = null, onFail = null, options = {} ) => {
    // Define the valid endpoints
    let validEndpoints = [ 'group_batch_has_name' ];

    // Check if the endPoint parameter is a valid endpoint
    if ( inArray( endPoint, validEndpoints ) ){
        // Do AJAX
        $.ajax({
            method:   'POST',
            url:      `${ UncannyCodes.restURL }/${endPoint}/`,
            data:     $.param( data ) + '&' + $.param({ doing_rest: 1 }),
            dataType: 'json',

            // Attach Nonce the the header of the request
            beforeSend: function( xhr ){
                xhr.setRequestHeader( 'X-WP-Nonce', UncannyCodes.nonce );
            },

            success: function( response ){
                // Check if the request succeeded
                if ( response.success ){
                    // Invoke callback
                    if ( isDefined( onSuccess ) ){
                        // Invoke callback
                        onSuccess( response );
                    }
                }
                else {
                    // The call was successful, but there were errors,
                    console.error( 'The call was successful, but there were errors.' );
                    // console.log( response );

                    // Check if the onFail callback is defined
                    if ( isDefined( onFail ) ){
                        // Invoke callback
                        onFail( response );
                    }
                }
            },

            statusCode: {
                403: function( response ){
                    if ( isDefined( onFail ) ){
                        onFail( response );
                    }
                }
            },

            error: function( jqXHR, textStatus, errorThrown ){
                if ( isDefined( onFail ) ){
                    onFail({
                        success: false,
                    });
                }
            },

            fail: function ( response ){
                if ( isDefined( onFail ) ){
                    onFail( response );
                }
            },
        });
    }
    else {
        console.error( vsprintf( 'The %s endPoint does not exists. The valid endpoints are %s', [ endPoint, validEndpoints.join( ', ' ) ] ) );
    }
}

/**
 * Determine if a variable is set and is not NULL
 *
 * @param  {mixed}      variable - The variable being evaluated
 * @return {boolean}    TRUE if the variable is defined
 */

export const isDefined = ( variable ) => {
	// Returns true if the variable is undefined
    return typeof variable !== 'undefined' && variable !== null;
}

/**
 * Determine whether a variable is empty
 *
 * @param   {mixed}     variable - The variable being evaluated
 * @return  {boolean}   TRUE if the variable is empty
 */

export const isEmpty = ( variable ) => {
    let response = true;

    // Check if the variable is defined, otherwise is empty
    if ( isDefined( variable ) ){
        // Check if it's array
        if ( variable.isArray ){
            response = variable.length == 0;
        }
        else if ( isObject( variable ) ){
            response = Object.keys( variable ).length;
        }
        else {
            response = variable == '';
        }
    }

    return response;
}

/**
 * Determine whether a variable is an object
 *
 * @param   {mixed}     variable - The variable being evaluated
 * @return  {boolean}   TRUE if the variable is an object
 */

export const isObject = ( variable ) => {
    return typeof variable === 'object' && variable !== null;
}

/**
 * Check if a value exists in an array
 * If the needle is an array then it will check if all his elements exists in the haystack
 *
 * @param   {mixed}     needle - The searched value. The comparison between values is strict. If the needle is an array then it will check if all his elements exists in the haystack
 * @param   {array}     haystack - An array through which to search.
 *
 * @return  {boolean} TRUE if needle is found in the array or if needle is an array, TRUE if all the elements from the needle are in haystack
 */

export const inArray  = ( needle, haystack ) => {
    let response = false;

    if ( Array.isArray( needle ) ){
        // Check that all the elements from the array "value" are in the array "array"
        response = arrayDifference( needle, haystack ).length == 0;
    }
    else {
        // Check if one element is in an array
        response = $.inArray( needle, haystack ) !== -1;
    }

    return response;
}

export const throttle = ( func, interval ) => {
    var lastCall = 0;

    return function(){
        var now = Date.now();
        if ( lastCall + interval < now ){
            lastCall = now;
            return func.apply( this, arguments );
        }
    };
};

export const debounce = ( func, interval ) => {
    var lastCall = -1;

    return function(){
        clearTimeout( lastCall );
        var args = arguments;
        var self = this;
        lastCall = setTimeout( function(){
            func.apply( self, args );
        }, interval );
    };
};

/**
 * Insert parameter to an URL.
 * If the parameter already exists it will update it
 * 
 * @param  {String} url        The URL
 * @param  {Object} parameters The parameters
 * @param  {Object} options    The options. It supports two properties, "setAsURL" and "refresh"
 * @return {String}            The URL with the parameters
 */
export function addParametersToURL( url = '', parameters = {}, options = {} ){
    // If the URL is empty, use the site URL
    if ( isEmpty( url ) ){
        url = window.location.href;
    }
    
    // Try to get an URL object
    try {
        url = new URL( url );
    } catch ( e ){
        // If something goes wrong, just return the original URL
        return url;
    }

    // Get the current parameters in the URL
    let urlParameters = url.search.substr( 1 ).split( '&' );

    // Iterate the parameters
    for ( let [ key, value ] of Object.entries( parameters ) ){
        key   = encodeURI( key );
        value = encodeURI( value );

        let i = urlParameters.length;
        let x;

        while ( i-- ){
            x = urlParameters[ i ].split( '=' );

            if ( x[ 0 ] == key ){
                x[ 1 ] = value;
                urlParameters[i] = x.join( '=' );
                break;
            }
        }

        if ( i < 0 ){
            urlParameters[ urlParameters.length ] = [ key, value ].join( '=' );
        }
    }

    // Remove empty parameters
    urlParameters = urlParameters.filter( parameter => ! isEmpty( parameter ) );

    // Add parameters to the URL
    url.search = urlParameters.join( '&' );

    // Set default options
    options = $.extend({}, {
        setAsURL: false,
        refresh:  false
    }, options );

    // Check if we have to set it as the current URL
    if ( options.setAsURL ){
        // Check if we have to reload
        if ( options.refresh ){
            // This will reload the page, it's likely better to store this until finished
            window.location.href = url.href; 
        }
        else {
            // Get title of the current page
            let pageTitle = document.title;

            // Push history and update URL
            window.history.pushState({}, pageTitle, url.href );
        }
    }

    // Return URL
    return url.href;
}